import { cn } from '@avanta/shared/utils/cn';
import { memo, type ReactElement } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { type ChartDataPoint } from 'types/chart';
import { CustomTooltip } from '../CustomTooltip';
import { CHART_COLORS, CHART_CONFIG } from './styles';

interface ChartProps {
  chartData: ChartDataPoint[];
  onWeekClick: (week: ChartDataPoint) => void;
}

const ChartComponent = ({ chartData, onWeekClick }: ChartProps): ReactElement => {
  const calculateYDomain = (chartData: ChartDataPoint[]): [number, number] => {
    if (!chartData.length) return [0, 5];

    const scores = chartData.map(item => item.score);
    const minScore = Math.min(...scores);
    const maxScore = Math.max(...scores);

    const margin = Math.max(1, (maxScore - minScore) * 0.1);
    const yMin = Math.max(0, Math.floor(minScore - margin));
    const yMax = Math.ceil(maxScore + margin);

    return [yMin, yMax];
  };

  const handleChartClick = (
    e: React.MouseEvent<HTMLDivElement>,
    chartData: ChartDataPoint[],
    onWeekClick: (week: ChartDataPoint) => void
  ): void => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const chartWidth = rect.width;
    const pointIndex = Math.round((x / chartWidth) * (chartData.length - 1));
    const clickedPoint = chartData[pointIndex];

    if (clickedPoint && clickedPoint.isWeeklyGroup && clickedPoint.weekData) {
      onWeekClick(clickedPoint);
    }
  };

  const yDomain = calculateYDomain(chartData);
  const yTicks = Array.from({ length: yDomain[1] - yDomain[0] + 1 }, (_, i) => yDomain[0] + i);

  const axisProps = {
    axisLine: { stroke: CHART_COLORS.axis, strokeWidth: CHART_CONFIG.strokeWidth.axis },
    tickLine: { stroke: CHART_COLORS.axis, strokeWidth: CHART_CONFIG.strokeWidth.axis },
  };

  const tickStyle = {
    fontSize: CHART_CONFIG.fontSize.axis,
    fill: CHART_COLORS.text,
  };

  return (
    <div
      className={cn(
        'w-full h-64 cursor-pointer focus:outline-none select-none',
        'hover:bg-gray-50/50 transition-colors duration-200',
        'focus:ring-2 focus:ring-teal-200 focus:ring-offset-2',
        'active:scale-[0.98] transition-transform duration-150'
      )}
      role="button"
      tabIndex={0}
      onMouseDown={e => e.preventDefault()}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
        }
      }}
      onClick={e => handleChartClick(e, chartData, onWeekClick)}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={CHART_CONFIG.margin}>
          <CartesianGrid
            strokeDasharray={CHART_CONFIG.gridDashArray}
            stroke={CHART_COLORS.grid}
            horizontal
            vertical
          />
          <XAxis
            dataKey="label"
            {...axisProps}
            tick={{ ...tickStyle, textAnchor: 'middle' }}
            tickMargin={8}
          />
          <YAxis
            domain={yDomain}
            ticks={yTicks}
            {...axisProps}
            tick={{ ...tickStyle, textAnchor: 'end', dominantBaseline: 'middle' }}
            tickMargin={CHART_CONFIG.tickMargin}
            width={CHART_CONFIG.yAxisWidth}
          />
          <Tooltip
            content={<CustomTooltip />}
            cursor={{
              stroke: CHART_COLORS.hover,
              strokeWidth: CHART_CONFIG.strokeWidth.hover,
              strokeDasharray: CHART_CONFIG.hoverDashArray,
            }}
          />
          <Line
            type="natural"
            dataKey="score"
            stroke={CHART_COLORS.primary}
            strokeWidth={CHART_CONFIG.strokeWidth.line}
            dot={{
              fill: CHART_COLORS.primary,
              stroke: 'none',
              r: CHART_CONFIG.dotRadius.normal,
            }}
            activeDot={{
              r: CHART_CONFIG.dotRadius.active,
              fill: CHART_COLORS.primary,
              stroke: CHART_COLORS.white,
              strokeWidth: CHART_CONFIG.strokeWidth.activeDot,
            }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export const Chart = memo(ChartComponent);
